import { useEffect } from 'react'

interface SEOProps {
  title: string
  description: string
  keywords: string
  canonical?: string
  ogImage?: string
  ogType?: string
  structuredData?: object
  additionalMeta?: Record<string, string>
  hreflang?: Record<string, string>
}

// Comprehensive keyword database for different page types
export const KEYWORD_LIBRARY = {
  pdf: [
    'PDF converter', 'PDF tools', 'PDF merger', 'PDF splitter', 'PDF compressor',
    'merge PDF', 'split PDF', 'compress PDF', 'PDF online', 'free PDF converter',
    'PDF editor', 'PDF reader', 'PDF creator', 'PDF generator', 'PDF processor',
    'PDF manipulator', 'PDF utility', 'PDF software', 'PDF application', 'PDF platform'
  ],
  powerpoint: [
    'PowerPoint to PDF', 'PPT to PDF', 'PPTX to PDF', 'presentation converter',
    'slides to PDF', 'PowerPoint converter', 'presentation to PDF online',
    'convert PowerPoint', 'PPT converter', 'slide deck converter',
    'presentation tools', 'PowerPoint online', 'slide converter',
    'presentation PDF', 'PowerPoint export', 'slide export'
  ],
  excel: [
    'Excel to PDF', 'XLS to PDF', 'XLSX to PDF', 'spreadsheet converter',
    'Excel converter', 'spreadsheet to PDF', 'workbook converter',
    'Excel online', 'spreadsheet tools', 'Excel export',
    'data converter', 'table converter', 'worksheet converter'
  ],
  word: [
    'Word to PDF', 'DOC to PDF', 'DOCX to PDF', 'document converter',
    'Word converter', 'document to PDF', 'text converter',
    'Word online', 'document tools', 'Word export',
    'text to PDF', 'document processor', 'writing tools'
  ],
  image: [
    'image converter', 'image resizer', 'image compressor', 'JPG to PDF',
    'PNG to PDF', 'image to PDF', 'photo converter', 'picture converter',
    'image tools', 'photo tools', 'image processor', 'graphic converter',
    'image optimizer', 'photo optimizer', 'image editor'
  ],
  technical: [
    'online converter', 'browser converter', 'web converter', 'HTML5 converter',
    'JavaScript converter', 'client-side converter', 'secure converter',
    'privacy converter', 'no upload converter', 'offline converter',
    'WebAssembly converter', 'progressive web app', 'PWA converter'
  ],
  quality: [
    'free converter', 'professional converter', 'high quality converter',
    'fast converter', 'reliable converter', 'accurate converter',
    'unlimited converter', 'no watermark converter', 'premium converter',
    'enterprise converter', 'business converter', 'commercial converter'
  ]
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://snap-tools.online/og-image.jpg',
  ogType = 'website',
  structuredData,
  additionalMeta = {},
  hreflang = {}
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title

    // Update meta description
    updateMetaTag('description', description)
    
    // Update keywords
    updateMetaTag('keywords', keywords)
    
    // Update additional meta tags
    Object.entries(additionalMeta).forEach(([name, content]) => {
      updateMetaTag(name, content)
    })
    
    // Update canonical link
    if (canonical) {
      updateCanonicalLink(canonical)
    }
    
    // Update hreflang links
    Object.entries(hreflang).forEach(([lang, href]) => {
      updateHreflangLink(lang, href)
    })
    
    // Update Open Graph tags
    updateMetaProperty('og:title', title)
    updateMetaProperty('og:description', description)
    updateMetaProperty('og:image', ogImage)
    updateMetaProperty('og:type', ogType)
    updateMetaProperty('og:url', canonical || window.location.href)
    updateMetaProperty('og:site_name', 'SnapTools')
    updateMetaProperty('og:locale', 'en_US')
    
    // Update Twitter Card tags
    updateMetaProperty('twitter:card', 'summary_large_image')
    updateMetaProperty('twitter:site', '@snaptools')
    updateMetaProperty('twitter:title', title)
    updateMetaProperty('twitter:description', description)
    updateMetaProperty('twitter:image', ogImage)
    
    // Add structured data
    if (structuredData) {
      updateStructuredData(structuredData)
    }
    
    // Clean up function
    return () => {
      // Cleanup if needed
    }
  }, [title, description, keywords, canonical, ogImage, ogType, structuredData, additionalMeta, hreflang])
}

const updateMetaTag = (name: string, content: string) => {
  let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
  if (!metaTag) {
    metaTag = document.createElement('meta')
    metaTag.setAttribute('name', name)
    document.head.appendChild(metaTag)
  }
  metaTag.setAttribute('content', content)
}

const updateMetaProperty = (property: string, content: string) => {
  let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
  if (!metaTag) {
    metaTag = document.createElement('meta')
    metaTag.setAttribute('property', property)
    document.head.appendChild(metaTag)
  }
  metaTag.setAttribute('content', content)
}

const updateCanonicalLink = (href: string) => {
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalLink)
  }
  canonicalLink.setAttribute('href', href)
}

const updateHreflangLink = (lang: string, href: string) => {
  let hreflangLink = document.querySelector(`link[hreflang="${lang}"]`) as HTMLLinkElement
  if (!hreflangLink) {
    hreflangLink = document.createElement('link')
    hreflangLink.setAttribute('rel', 'alternate')
    hreflangLink.setAttribute('hreflang', lang)
    document.head.appendChild(hreflangLink)
  }
  hreflangLink.setAttribute('href', href)
}

const updateStructuredData = (data: object) => {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"][data-page-specific]')
  if (existingScript) {
    existingScript.remove()
  }
  
  // Add new structured data
  const script = document.createElement('script')
  script.setAttribute('type', 'application/ld+json')
  script.setAttribute('data-page-specific', 'true')
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

// SEO utility functions for different page types
export const createToolPageStructuredData = (toolName: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": toolName,
  "url": url,
  "description": description,
  "applicationCategory": "ProductivityApplication",
  "operatingSystem": "Web Browser",
  "browserRequirements": "Modern web browser with JavaScript enabled",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "publisher": {
    "@type": "Organization",
    "name": "SnapTools",
    "url": "https://snap-tools.online",
    "logo": {
      "@type": "ImageObject",
      "url": "https://snap-tools.online/logo.png"
    }
  },
  "potentialAction": {
    "@type": "UseAction",
    "target": url
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "1250",
    "bestRating": "5"
  }
})

export const createBreadcrumbStructuredData = (breadcrumbs: Array<{name: string, url: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
})

// Generate comprehensive keywords for specific tool types
export const generateKeywords = (toolType: keyof typeof KEYWORD_LIBRARY, additionalKeywords: string[] = []): string => {
  const baseKeywords = KEYWORD_LIBRARY[toolType] || []
  const generalKeywords = [
    ...KEYWORD_LIBRARY.technical,
    ...KEYWORD_LIBRARY.quality
  ]
  
  const allKeywords = [
    ...baseKeywords,
    ...generalKeywords.slice(0, 10), // Limit general keywords
    ...additionalKeywords
  ]
  
  return allKeywords.join(', ')
}
